import { useEffect, useRef, useState } from 'react';

/**
 * CleoLead "Warm Editorial" motion system — scroll-reveal primitives.
 *
 * Motion tokens (see DESIGN.md / theme.ts `tokens.motion`):
 * - reveal: 400ms, easing cubic-bezier(0.16, 1, 0.3, 1) (expo-out)
 * - reveals fire ONCE, at 20% viewport intersection
 * - sibling stagger: 70ms
 * - every animation respects prefers-reduced-motion
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Reactively tracks the user's prefers-reduced-motion setting. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14 fallback
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return reduced;
}

export interface UseRevealOptions {
  /** Intersection threshold; default 0.2 (20% of element in viewport). */
  threshold?: number;
  /** Optional IntersectionObserver rootMargin. */
  rootMargin?: string;
}

export interface UseRevealResult<T extends HTMLElement> {
  /** Attach to the element that should reveal. */
  ref: React.RefObject<T | null>;
  /** True once the element has entered the viewport (or motion is reduced). */
  revealed: boolean;
  /** True when the user prefers reduced motion — render final state instantly. */
  reduced: boolean;
}

/**
 * Reveal-once IntersectionObserver hook.
 *
 * - Fires a single time when 20% of the element is visible, then disconnects.
 * - If the user prefers reduced motion (or IntersectionObserver is missing),
 *   the element is treated as revealed immediately.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseRevealOptions = {}
): UseRevealResult<T> {
  const { threshold = 0.2, rootMargin } = options;
  const ref = useRef<T | null>(null);
  const reduced = usePrefersReducedMotion();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return undefined;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return undefined;
    }

    const el = ref.current;
    if (!el) {
      setRevealed(true);
      return undefined;
    }

    // Above-the-fold content must reveal on load WITHOUT depending on the
    // IntersectionObserver's initial callback — which browsers throttle/defer
    // for backgrounded or just-restored tabs (and which would otherwise leave
    // the hero invisible until the first scroll). This synchronous viewport
    // check runs in a normal (non-throttled) effect, so anything already on
    // screen fades in immediately; below-the-fold nodes still use the observer.
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.top < vh && rect.bottom > 0) {
      setRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [revealed, reduced, threshold, rootMargin]);

  return { ref, revealed, reduced };
}

export default useReveal;
